import { useMutation } from "@apollo/client";
import { StoreOutlined } from "@mui/icons-material";
import { TextField, Typography } from "@mui/material";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { REQUEST_DEACTIVATE_COMPANY } from "graphql/company";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getGraphqlErrorMessage } from "utils/graphql";
import { Stack } from "components/layout/reusableLayouts";
import { ConfirmDialog } from "@outpave/ds";

interface DeactivateAccountModalProps {
  openDeactivateAccountModal: boolean;
  setOpenDeactivateAccountModal: (open: boolean) => void;
  accountId?: string;
  accountName: string;
  handleUpdateAccount?: () => void;
}

export const DeactivateAccountModal: React.FC<DeactivateAccountModalProps> = ({
  openDeactivateAccountModal,
  setOpenDeactivateAccountModal,
  accountId,
  accountName,
  handleUpdateAccount,
}) => {
  const { t } = useTranslation();
  const [requestedReason, setRequestedReason] = useState("");

  const [deactivateCompany, { loading }] = useMutation(
    REQUEST_DEACTIVATE_COMPANY,
    {
      onCompleted: (data) => {
        toast.success(t("platform.requestDeactivateAccountSuccess"));
        handleUpdateAccount && handleUpdateAccount();
        setOpenDeactivateAccountModal(false);
      },
      onError: (error) => {
        const errorMessage = error?.graphQLErrors
          ? getGraphqlErrorMessage(error?.graphQLErrors)
          : t("Please try again!");
        toast.error(errorMessage);
      },
    },
  );

  return (
    <>
      <ConfirmDialog
        open={openDeactivateAccountModal}
        tone="danger"
        title={t("platform.deactivateAccount")}
        icon={<StoreOutlined />}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={async () => {
          try {
            setOpenDeactivateAccountModal(false);
            await deactivateCompany({
              variables: {
                id: accountId,
                ...(requestedReason !== "" && { reason: requestedReason }),
              },
            });
          } catch (err: any) {
            console.error(err);
            toast.error(
              err.message || t("platform.failedToRequestDeactivateAccount"),
            );
          }
        }}
        onCancel={() => {
          setOpenDeactivateAccountModal(false);
        }}
        message={
          <Stack gap="var(--space-4)">
            <Trans
              i18nKey="platform.deactivateAccountConfirmMessage"
              components={{
                account_name: (
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={{ color: "var(--color-text-brand)" }}
                  />
                ),
              }}
              values={{ accountName: accountName }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={2}
              label={t("platform.reasonForRequest")}
              placeholder={t("platform.reason")}
              name="notes"
              value={requestedReason}
              onChange={(e) => setRequestedReason(e.target.value)}
            />
          </Stack>
        }
      />
      <LoadingWithoutModal open={loading} />
    </>
  );
};
